import {
  Alert,
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import * as React from "react";
import { useState, useEffect } from "react";
import TopBar from "../../Layout/components/TopBar/TopBar";
import styles from "./EditProfileView.module.scss";
import { useEditProfile } from "../hooks/useEditProfile";
import usePageTitle from "../../Common/hooks/usePageTitle";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";

const NUMERIC_REGEX = /\d/;
const MAX_DISPLAY_NAME_LENGTH = 255;

const EditProfileView = (): JSX.Element => {
  usePageTitle("Edit profile");

  const { profile, isLoading, isSaving, error, saveSuccess, saveDisplayName } =
    useEditProfile();

  const [displayName, setDisplayName] = useState("");
  const [displayNameError, setDisplayNameError] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (profile) {
      setDisplayName(profile.atlassianDisplayName);
      setIsDirty(false);
    }
  }, [profile]);

  useEffect(() => {
    if (saveSuccess) {
      setIsDirty(false);
    }
  }, [saveSuccess]);

  const validateDisplayName = (value: string): string | null => {
    if (!value.trim()) return "Display name is required";
    if (value.trim().length > MAX_DISPLAY_NAME_LENGTH)
      return `Display name cannot exceed ${MAX_DISPLAY_NAME_LENGTH} characters`;
    if (NUMERIC_REGEX.test(value))
      return "Display name must not contain numeric values";
    return null;
  };

  const handleDisplayNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setDisplayName(val);
    setDisplayNameError(validateDisplayName(val));
    setIsDirty(true);
  };

  const handleSave = () => {
    const validationError = validateDisplayName(displayName.trim());
    if (validationError) {
      setDisplayNameError(validationError);
      return;
    }
    saveDisplayName(displayName.trim());
  };

  const isUnchanged = profile
    ? displayName.trim() === profile.atlassianDisplayName
    : true;
  const isSaveDisabled =
    isSaving ||
    !displayName.trim() ||
    !!displayNameError ||
    isUnchanged ||
    !isDirty;

  return (
    <div style={{ height: "100%" }}>
      <TopBar>
        <div className={styles.pageContainer__topBarContainer}>
          <PersonOutlineIcon fontSize="large" />
          <Typography variant="h4">Edit profile</Typography>
        </div>
      </TopBar>

      {isLoading && <Backdrop open={isLoading} />}

      <div className={styles.pageContainer}>
        {error && !isLoading && (
          <Alert severity="error" sx={{ marginBottom: 2 }}>
            {error}
          </Alert>
        )}
        {saveSuccess && !isDirty && (
          <Alert severity="success" sx={{ marginBottom: 2 }}>
            Display name updated successfully.
          </Alert>
        )}

        {!isLoading && profile && (
          <Card className={styles.profileCard} variant="outlined">
            <CardContent sx={{ padding: 3 }}>
              {/* Atlassian display name */}
              <Box className={styles.fieldSection}>
                <Typography
                  variant="body2"
                  className={styles.fieldLabel}
                  gutterBottom
                >
                  Atlassian display name
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={displayName}
                  onChange={handleDisplayNameChange}
                  placeholder="Enter your Atlassian display name"
                  disabled={isSaving}
                  error={!!displayNameError}
                  helperText={displayNameError ?? " "}
                />
                <Typography
                  variant="caption"
                  className={styles.infoNote}
                  display="block"
                >
                  This is the display name shown in your Jira / Atlassian
                  account.
                </Typography>
              </Box>

              <Box className={styles.saveRow}>
                <Button
                  variant="contained"
                  disableElevation
                  onClick={handleSave}
                  disabled={isSaveDisabled}
                  startIcon={
                    isSaving ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : undefined
                  }
                >
                  {isSaving ? "Saving…" : "Save changes"}
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default EditProfileView;
